/**
 * Clause review / carry-from / redline API — typed calls over the shared
 * axios instance (`services/api`), for the Contracts tab (contracts-plan.md
 * §9.3, §9.4, §5.9, §13.7 WP-62b; session 4 SHARED CONTRACT BLOCKS 1-4).
 *
 * Never imports axios directly — only `services/api`, the same seam every
 * other module under `frontend/src/contracts/api/` uses, and never edits
 * `api/client.ts` (the re-export line ships in this WP's `.mount.md` sidecar
 * instead, applied by a later MOUNT step).
 *
 * NAME CHECK (session 4 FACTS 9, the MOUNT-1 "duplicate NegotiationKind"
 * lesson): every name below was checked against `client.ts` and every module
 * it re-exports (`rateCard.ts`, `notes.ts`, `medicare.ts`, `industry.ts`,
 * `fees.ts`, `intake.ts`, `workspace.ts`, `metrics.ts`, `versions.ts`,
 * `calculator.ts`) before it was written here — none of them declare any name
 * in this file (grepped 2026-09-12; the taken names this file deliberately
 * avoids: `G8Label` [versions.ts], `MetricLabel` [metrics.ts], `FeeLabel`
 * [fees.ts], `RcLabel` [rateCard.ts], `MedicareLabel` [medicare.ts],
 * `NoteProvenance` [notes.ts], `VersionDirection`/`VersionSource`
 * [intake.ts], `getVersion`/`markVersionExecuted`/`supersedeVersion`
 * [workspace.ts / versions.ts]). See this tab folder's `contracts.mount.md`
 * for the full list of names exported here, restated for the mount agent's
 * own re-check.
 *
 * `Study`, `Negotiation`, `NegotiationKind`, `listStudies`, `listNegotiations`
 * live in `./client`; `ContractsVersion`, `listNegotiationVersions`,
 * `downloadVersion` live in `./workspace` — this file imports those where it
 * needs them (VersionPicker.tsx / CarryFromDialog.tsx do), and never
 * redeclares them.
 */
import api from '../../services/api';

// ---------------------------------------------------------------------------
// The clause list — GET /versions/:id/clauses (mounted, WP-62a). Session 4
// SHARED CONTRACT BLOCK 4 step 2; FACTS 4 (flags), FACTS 5 (similar), FACTS 6
// (carried_from).
// ---------------------------------------------------------------------------

/**
 * A topic slug. Deliberately `string`, not a union of the 24
 * `REVIEW_TOPICS` slugs (backend/src/services/contracts/clauses/review.js) —
 * the backend owns that list; typing it here as a closed union would make an
 * unlisted slug (a future addition on the backend side) a TypeScript error
 * instead of something `topicLabel()` can still render (session 4 FACTS 2).
 */
export type ClauseTopic = string;

export type ClauseFlagKind = 'numeric' | 'disagreement' | 'text' | 'required_element';
export type ClauseFlagSeverity = 'major' | 'minor' | 'disagreement';
export type ClauseFlagScope = 'clause' | 'document';
export type ClauseFlagComparator = 'max' | 'min' | 'eq' | 'text';
export type ClauseFlagBasis = 'playbook' | 'rate_card' | 'brief' | 'user';

/**
 * `review.flags[]` — §5.9's four keys (`position_id`, `found_value`,
 * `position_value`, `severity`) always present, plus what this screen needs
 * to render a flag beside its position without a second query (session 4
 * FACTS 4). `position_id: null` is a real, renderable flag (the positions
 * library holds nothing for that topic yet) — never filtered out.
 */
export interface ClauseFlag {
  position_id: string | null;
  found_value: number | string | null;
  position_value: number | string | null;
  severity: ClauseFlagSeverity;
  kind: ClauseFlagKind;
  scope: ClauseFlagScope;
  topic: ClauseTopic;
  term_class: string | null;
  found_unit: string | null;
  position_unit: string | null;
  comparator: ClauseFlagComparator | null;
  basis: ClauseFlagBasis | null;
  position_text: string | null;
  clause_id: string;
  clause_path: string | null;
  message: string;
}

/** `review.similar[]` — §5.9's shape exactly, nothing else (session 4 FACTS 5). */
export interface ClauseSimilarMatch {
  accepted_language_id: string;
  score: number;
}

/** `review.carried_from` (§5.9, R39; session 4 FACTS 6). */
export interface ClauseCarriedFrom {
  study_id: string;
  version_id: string;
  clause_id: string;
  date: string | null;
  carried_at: string;
  clause_path: string | null;
  proposed_text: string;
}

export type ClauseDecision = 'accept' | 'change' | 'escalate';

/** One `cta_clause_reviews` row, as `clauses[i].review` carries it. */
export interface ClauseReview {
  id: string;
  clause_id: string;
  version_id: string;
  topic: ClauseTopic | null;
  numeric_terms: unknown[];
  flags: ClauseFlag[];
  similar: ClauseSimilarMatch[];
  decision: ClauseDecision | null;
  decided_by: string | null;
  decided_at: string | null;
  carried_from: ClauseCarriedFrom | null;
}

/** One `cta_clauses` row plus its latest review (or `null` when never reviewed). */
export interface ClauseRow {
  id: string;
  version_id: string;
  parse_run_id: string;
  ordinal: number;
  path: string | null;
  heading: string | null;
  text: string;
  normalized_hash: string;
  has_insertions: boolean;
  has_deletions: boolean;
  comments: unknown[];
  provenance: Record<string, unknown>;
  review: ClauseReview | null;
}

export interface ListClausesResult {
  version_id: string;
  count: number;
  reviewed_count: number;
  clauses: ClauseRow[];
}

/** GET /versions/:id/clauses (mounted). A never-reviewed version answers `reviewed_count: 0` and every clause's `review: null` — the honest answer, not a refusal. */
export async function listVersionClauses(versionId: string): Promise<ListClausesResult> {
  const response = await api.get<ListClausesResult>(`/api/contracts/versions/${versionId}/clauses`);
  return response.data;
}

// ---------------------------------------------------------------------------
// Run the review — POST /versions/:id/review (mounted, WP-62a). SHARED
// CONTRACT BLOCK 4 step 3. `screen_fail_payment` and `coverage` live only on
// this response, never on the clause-list GET.
// ---------------------------------------------------------------------------

export interface ReviewFlagCounts {
  major: number;
  minor: number;
  disagreement: number;
}

export interface ReviewScreenFailPayment {
  addressed: boolean;
  verdict: string | null;
  addressed_by: string | null;
}

export interface ReviewCoverage {
  clauses: number;
  terms: number;
  positions_considered: number;
  accepted_language_considered: number;
  uncompared: unknown[];
}

export interface ReviewSummary {
  version_id: string;
  network_id: string;
  study_id: string;
  filename: string | null;
  reviewed_at: string;
  topic_label: 'inferred';
  clause_count: number;
  review_ids: string[];
  flags: ClauseFlag[];
  document_flags: ClauseFlag[];
  flag_counts: ReviewFlagCounts;
  topics: Record<string, number>;
  required_elements: unknown[];
  screen_fail_payment: ReviewScreenFailPayment;
  coverage: ReviewCoverage;
}

/** POST /versions/:id/review (mounted). */
export async function reviewVersionClauses(versionId: string): Promise<ReviewSummary> {
  const response = await api.post<{ review: ReviewSummary }>(`/api/contracts/versions/${versionId}/review`);
  return response.data.review;
}

// ---------------------------------------------------------------------------
// Decisions and the single-clause carry — PUT /clauses/:id/decision, POST
// /clauses/:id/carry (both mounted, WP-62a). `:id` on both is a
// `cta_clause_reviews` id (`clauses[i].review.id`), never a clause id.
// ---------------------------------------------------------------------------

/** PUT /clauses/:clauseReviewId/decision, body { decision }. */
export async function decideClauseReview(clauseReviewId: string, decision: ClauseDecision): Promise<ClauseReview> {
  const response = await api.put<{ review: ClauseReview }>(`/api/contracts/clauses/${clauseReviewId}/decision`, { decision });
  return response.data.review;
}

/** POST /clauses/:clauseReviewId/carry, body { fromClauseId } — WP-62a's own mounted per-clause carry (distinct from the topic-based carry-from dialog below). */
export async function carryClauseFromClause(clauseReviewId: string, fromClauseId: string): Promise<ClauseReview> {
  const response = await api.post<{ review: ClauseReview }>(`/api/contracts/clauses/${clauseReviewId}/carry`, { fromClauseId });
  return response.data.review;
}

// ---------------------------------------------------------------------------
// The carry-from dialog — GET/POST /versions/:id/carry-from/:sourceVersionId
// (session 4 SHARED CONTRACT BLOCK 1). WP-63 builds and owns both routes;
// this WP only calls them. THEY ARE DEFERRED TO A MOUNT STEP — in production
// today, and until `backend/src/routes/contracts/carry.mount.md` is applied
// alongside this tab folder's own `contracts.mount.md`, both answer 404. That
// is not this file's bug to swallow: `describeClausesApiError` below turns a
// 404 into "not mounted yet, or no such record" plus the server's own
// message, and the dialog renders it visibly (session 4 DECISION f, G13).
// ---------------------------------------------------------------------------

export interface CarryFromClauseSource {
  clause_id: string;
  path: string | null;
  heading: string | null;
  ordinal: number;
  excerpt: string;
}

export interface CarryFromClauseTarget extends CarryFromClauseSource {
  clause_review_id: string;
  decision: ClauseDecision | null;
}

export interface CarryFromPair {
  clause_review_id: string;
  from_clause_id: string;
}

export interface CarryFromTopicGroup {
  topic: ClauseTopic;
  source_clauses: CarryFromClauseSource[];
  target_clauses: CarryFromClauseTarget[];
  pairs: CarryFromPair[];
  unpaired_source: string[];
  unpaired_target: string[];
}

export interface CarryFromTargetInfo {
  version_id: string;
  filename: string | null;
  version_date: string | null;
  study_id: string;
  protocol_number: string | null;
  reviewed: boolean;
  review_count: number;
}

export interface CarryFromSourceInfo {
  version_id: string;
  filename: string | null;
  version_date: string | null;
  study_id: string;
  protocol_number: string | null;
  is_executed: boolean;
  document_kind: string;
  topic_source: 'review' | 'computed';
}

export interface CarryFromPreview {
  target: CarryFromTargetInfo;
  source: CarryFromSourceInfo;
  topics: CarryFromTopicGroup[];
  counts: { source_clauses: number; target_clauses: number; topics: number; pairs: number };
  labels: { topic: 'inferred' };
  not_carried: string[];
}

/** GET /versions/:id/carry-from/:sourceVersionId — deferred mount; read-only, writes nothing. */
export async function getCarryFromPreview(versionId: string, sourceVersionId: string): Promise<CarryFromPreview> {
  const response = await api.get<CarryFromPreview>(`/api/contracts/versions/${versionId}/carry-from/${sourceVersionId}`);
  return response.data;
}

export interface CarryFromCarriedRow {
  topic: ClauseTopic;
  clause_id: string;
  clause_review_id: string;
  replaced_review_id: string;
  from_clause_id: string;
  decision: null;
  carried_from: ClauseCarriedFrom;
  status: 'carried';
}

export interface CarryFromSkippedRow {
  topic: ClauseTopic;
  reason: string;
  clause_id: string | null;
  from_clause_id: string | null;
  status: 'skipped';
}

export interface CarryFromResult {
  target_version_id: string;
  source_version_id: string;
  carried: CarryFromCarriedRow[];
  skipped: CarryFromSkippedRow[];
  counts: { requested_topics: number; carried: number; skipped: number };
  labels: { topic: 'inferred' };
  nothing_accepted: boolean;
}

/** POST /versions/:id/carry-from/:sourceVersionId, body { topics } — deferred mount; `topics` is REQUIRED and never defaulted (G3). */
export async function carryFromVersion(versionId: string, sourceVersionId: string, topics: string[]): Promise<CarryFromResult> {
  const response = await api.post<CarryFromResult>(`/api/contracts/versions/${versionId}/carry-from/${sourceVersionId}`, { topics });
  return response.data;
}

// ---------------------------------------------------------------------------
// Redline export — POST /versions/:id/redline (mounted, WP-64; session 4
// SHARED CONTRACT BLOCK 3). Neither this WP nor WP-63 changes anything about
// it. The response carries no bytes — the file is downloaded through
// api/workspace.ts's `downloadVersion` against the NEW sent version id.
// ---------------------------------------------------------------------------

export interface RedlineExportSummary {
  mode: 'redline';
  baseVersionId: string;
  clausesRedlined: number;
  mergedParagraphs: number;
  deletedParagraphs: number;
  skipped: unknown[];
  author: string;
  date: string;
  changesApplied: number;
  insertions: number;
  deletions: number;
  commentsWritten: number;
  paragraphsTouched: number;
  paragraphsTotal: number;
  parts: { modified: unknown[]; added: unknown[]; removed: unknown[] };
}

export interface RedlineExportResult {
  versionId: string;
  storageKey: string;
  filename: string;
  summary: RedlineExportSummary;
  version: Record<string, unknown>;
  duplicateWarning: string | null;
  skipped: unknown[];
}

/** Exactly one of these two — the tab never sends the raw `{ changes: [...] }` form other callers use. */
export type RedlineExportInput = { all: true } | { clauseReviewIds: string[] };

/** POST /versions/:id/redline (mounted, WP-64). */
export async function exportRedline(versionId: string, input: RedlineExportInput): Promise<RedlineExportResult> {
  const response = await api.post<RedlineExportResult>(`/api/contracts/versions/${versionId}/redline`, input);
  return response.data;
}

// ---------------------------------------------------------------------------
// Loud failure (G13) — api/versions.ts's `describeVersionsApiError` pattern,
// copied (this WP owns none of versions.ts's names, so it is copied rather
// than imported, same as versions.ts itself copied it from workspace.ts).
// ---------------------------------------------------------------------------

interface ApiErrorLike {
  response?: { status?: number; data?: { error?: string } };
}

export function describeClausesApiError(method: string, path: string, err: unknown): string {
  const apiErr = err as ApiErrorLike | undefined;
  const status = apiErr?.response?.status;
  const serverMessage = apiErr?.response?.data?.error;
  if (status === 404) {
    return `${method} ${path} — 404: ${
      typeof serverMessage === 'string' && serverMessage.length > 0 ? serverMessage : 'not mounted yet, or no such record'
    }.`;
  }
  if (typeof serverMessage === 'string' && serverMessage.length > 0) return serverMessage;
  return `${method} ${path} failed.`;
}
